import React, { Fragment, useMemo, useCallback } from 'react';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import { useAppSelector } from '../../../state/store';
import {
  getActiveMarker,
  getCurrentLocation,
  getLocationsById,
  getSensorsById,
  getSensorsByLocId,
} from '../../../state/selectors';
import { getLocationNameTree } from '../../../utils/locations';
import useGlobalStyles from '../../../styles';
import SensorListItem from './SensorListItem';

const sensorDisplayLimit = 250; // as we might not want to load all sensors, specially from root location

interface SensorsInSubLocProps {
  taggedSensors: string[];
  handleTagging: (value: string) => void;
  showAllSensors: boolean;
}

function SensorsInSubLoc({
  taggedSensors,
  handleTagging,
  showAllSensors,
}: SensorsInSubLocProps): JSX.Element {
  const { classes: globalClasses } = useGlobalStyles();
  const locationsById = useAppSelector(getLocationsById);
  const sensorsById = useAppSelector(getSensorsById);
  const currentLocation = useAppSelector(getCurrentLocation);
  const sensorsByLocId = useAppSelector(getSensorsByLocId);
  const activeMarker = useAppSelector(getActiveMarker);

  const locSensorsRecord = useMemo(() => {
    const sensorRecord = new Map<string, string[]>();
    const allSubLocations = Array.from(locationsById.keys())
      .sort()
      .filter((item) => (currentLocation === '#' ? true : item.startsWith(`${currentLocation}#`)));

    allSubLocations.reduce((totalCount, location) => {
      // once the budget is used up, skip remaining locations entirely
      if (totalCount >= sensorDisplayLimit) return totalCount;

      const allLocSensors = sensorsByLocId.get(location) ?? [];

      // filter out sensors already selected for grouping
      const filteredSensors = allLocSensors.filter((sensor) => !taggedSensors.includes(sensor));

      const activeMarkerSensors = filteredSensors.filter((sensor) =>
        sensorsById.get(sensor)?.data?.some((datum) => datum.varName === activeMarker)
      );

      // show all sensors or only active-marker sensors based on selection
      const sensorsList = showAllSensors ? filteredSensors : activeMarkerSensors;

      if (sensorsList.length === 0) return totalCount;

      // cap this location's contribution to whatever budget remains
      const remainingBudget = sensorDisplayLimit - totalCount;
      const cappedSensorsList = sensorsList.slice(0, remainingBudget);

      sensorRecord.set(location, cappedSensorsList);
      return totalCount + cappedSensorsList.length;
    }, 0);

    return sensorRecord;
  }, [
    activeMarker,
    currentLocation,
    taggedSensors,
    locationsById,
    sensorsById,
    sensorsByLocId,
    showAllSensors,
  ]);

  const renderLocationBreadCrumb = useCallback(
    (locId: string) => {
      const breadCrumb = getLocationNameTree(locId, locationsById);
      const locIndex = breadCrumb.findIndex((item) => item.id === currentLocation);
      const filteredTree = locIndex !== -1 ? breadCrumb.slice(locIndex + 1) : [];

      return (
        <Typography variant="h6" sx={{ paddingLeft: '5px', display: 'flex' }}>
          {filteredTree.map((subLocation, i) => (
            <span key={subLocation.id}>
              <span className={filteredTree.length > 2 ? globalClasses.ellipsisLocName : ''}>
                {locationsById.get(subLocation.id)?.name ?? ''}
              </span>
              <span
                className={filteredTree.length > 2 ? globalClasses.ellipsisLocName : ''}
                style={{ padding: '0 5px' }}
              >
                {i !== filteredTree.length - 1 && '-'}
              </span>
            </span>
          ))}
        </Typography>
      );
    },
    [currentLocation, globalClasses.ellipsisLocName, locationsById]
  );

  return (
    <Box sx={{ marginTop: '10px' }}>
      {locSensorsRecord.size > 0 ? (
        <>
          <Typography variant="h6">Sensors In Sub Location (s)</Typography>
          <Box sx={{ marginTop: '10px' }}>
            {Array.from(locSensorsRecord.entries()).map(([location, locationSensors]) => {
              const sortedLocSensors = [...locationSensors].sort((a, b) => {
                const nameA = sensorsById.get(a)?.name || a;
                const nameB = sensorsById.get(b)?.name || b;
                return nameA.localeCompare(nameB);
              });
              return (
                <Fragment key={location}>
                  {location && renderLocationBreadCrumb(location)}
                  <List>
                    {sortedLocSensors.map((sensor) => (
                      <SensorListItem
                        key={sensor}
                        sensorId={sensor}
                        handleTagging={handleTagging}
                      />
                    ))}
                  </List>
                  <hr />
                </Fragment>
              );
            })}
          </Box>
        </>
      ) : (
        <Alert severity="error">No sensors available in Sub location</Alert>
      )}
    </Box>
  );
}

export default SensorsInSubLoc;
